<script lang="ts">
  // #region imports
  import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
  import Flash from "@shared/services/flashv2.js";
  import * as _ from "lodash";
  import { params, redirect } from "@roxi/routify";

  // components
  import BreadcrumbNavigation from "@retailer/svelte/BreadcrumbNavigation/BreadcrumbNavigation.svelte";
  import LeftPane from "@retailer/svelte/ProductDetails/Containers/LeftPane.svelte";
  import RightPane from "@retailer/svelte/ProductDetails/Containers/RightPane.svelte";
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import ConnectDistributorModal from "@retailer/svelte/Settings/DistributorContacts/ConnectDistributorModal/ConnectDistributorModal.svelte";
  import { OnLayoutChange } from "@shared/svelte/Directives/OnLayoutChange.js";
  import * as ProductHelpers from "@retailer/svelte/ProductDetails/ProductHelpers.svelte";
  import DiscoveryPanel from "@shared/svelte/DiscoveryPanel/DiscoveryPanel.svelte";

  // services
  import * as DealNoteService from "@shared/services/deal_note";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { SalesRepsEventEntity } from "@shared/services/event_tracking/entities";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  // stores
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import DiscountsLookupStore from "@retailer/svelte/stores/DiscountsLookupStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { get as getStoreValue } from "svelte/store";
  import { getPreselectedProductAttributes } from "@retailer/svelte/ProductDetails/stores/ProductLineNavigationStore";

  import type { DiscountResult } from "@retailer/services/discounts_service";
  import type { Distributor } from "@shared/types/Distributor";
  import type { ProductLine, Product } from "@shared/types/ProductLine";
  import type { RetailerDistributor } from "@shared/types/RetailerDistributor";
  import type { SalesRep } from "@shared/types/SalesRep";
  import type { SfProduct, VendorProduct } from "@shared/types/SevenFifty";

  // props
  export let pricingDataReady: boolean = false;
  export let callbacks: any;
  export let distributorChoices: Distributor[];
  export let productLine: ProductLine;
  export let products: Product[];
  export let distributor: Distributor;
  export let selectDistributor: (distributor: Distributor) => void;
  export let sevenFiftyProduct: SfProduct;
  export let vendorProduct: VendorProduct;
  export let salesReps: SalesRep[];
  export let retailerDistributor: RetailerDistributor;
  export let accountDataLoaded: boolean = false;
  export let Cart;

  // data
  const { preSelectedContainerType, preSelectedVintage } = getPreselectedProductAttributes();

  let containerTypes: string[] = [];
  let formErrors;
  let hasDiscount: boolean;
  let mobileMode: boolean = null;
  let productsByContainer: { [key: string]: Product[] } = {};
  let selectedContainerType: string;
  let selectedVintage: string = $params.vintage;
  let selectedVintageProduct: Product;
  let showConnectDistributorModal: boolean = false;
  let showDeals: boolean;
  let showRecentlyOrdered: boolean;
  let alwaysChooseForceDistributorSelection: boolean = false;

  let showDualProductExperience: boolean =
    stagedRolloutCheck("dual_products") && productLine.dual_product;

  const { trackEvent } = EventTracking;
  const stateSettings = getStoreValue(StateSettingsStore);
  const usesFuturePricingWithMinBottle = stateSettings
    ? stateSettings["uses_future_pricing_with_min_bottle"]
    : false;

  callbacks.getEnglishDescription = product => {
    let ppdKeys = {};
    let array: {
      id?: number | string;
      name: string;
      key?: string;
      // Should these items have keys?
    }[] = [
      { id: null, name: "" },
      { id: "", name: "Non Glossy" },
    ];
    if (_.get(window, "sharedContext.productPackagingDescriptions")) {
      // Should we deconstruct the `array`?
      // @ts-ignore
      array = [array, ...window.sharedContext.productPackagingDescriptions];
    } else {
      array = [
        { id: null, name: "" },
        { id: "", name: "Non Glossy" },
        { id: "glossy", name: "Glossy" },
        { id: "tray_pack", name: "Tray Pack" },
        { id: "on_premise", name: "On Premise" },
        { id: "off_premise", name: "Off Premise" },
      ];
    }

    for (const ppd of array) ppdKeys[ppd.key] = ppd;
    return ppdKeys[product.packaging_description]
      ? ppdKeys[product.packaging_description].name
      : "";
  };

  callbacks.saveDeal = product => {
    DealNoteService.updateDeal(product, function (err, res) {
      if (err) {
        Flash.now({ type: "error", message: err });
      } else {
        window["stateService"].reload();
      }
    });
  };

  callbacks.distributorSubmit = async (distributor, reps, errorCallback) => {
    try {
      const res = await HttpPromise({
        method: "POST",
        url: "/api/retailer/distributors/" + distributor.id + "/sales_rep_assignments/replace",
        data: {
          replace: {
            sales_rep_ids: reps.map(r => r.id),
          },
        },
      });

      DistributorsStore.pushAddedSalesReps(distributor.id, reps);

      let header;
      let msg;

      if (!distributor.sales_reps || distributor.sales_reps.length == 0) {
        header = "Distributor connected";
        msg = `You can now place orders with ${distributor.name}. Manage your sales reps in Settings → My Distributors`;
      } else {
        msg = repsUpdatedMessage(distributor, res);
        header = "Sales reps updated";
        trackEvent(
          "Sales Reps Added",
          new SalesRepsEventEntity({
            service: "Retailer",
            component: "Distributor Settings",
            ids: distributor.sales_reps.map(rep => rep.id),
          }),
        );
      }
      Flash.now({ type: "success", message: msg, timeout: 10000, header });
      window["stateService"].reload();
    } catch (error) {
      Flash.now({
        type: "error",
        message: "An unexpected error occured while trying to add your sales reps.",
      });
      if (errorCallback) errorCallback(error);
    }
  };

  let repsUpdatedMessage = (distributor, reps) => {
    let addedMessage = "";
    let removedMessage = "";
    if (reps.added.length > 0)
      addedMessage = `${reps.added.join(", ")} added to ${distributor.name}. `;
    if (reps.removed.length > 0)
      removedMessage = `${reps.removed.join(", ")} removed from ${distributor.name}.`;
    return addedMessage + removedMessage;
  };

  callbacks.addNewSalesRep = async (distributor, newRep, errorCallback) => {
    try {
      const res = await HttpPromise({
        method: "POST",
        url: "/api/retailer/distributors/" + distributor.id + "/sales_reps",
        data: {
          sales_rep: {
            name: newRep.name,
            contact_phone: newRep.contact_phone,
            contact_email: newRep.contact_email,
            skip_assignment: true,
          },
        },
      });

      if (!res.id) {
        Flash.now({
          type: "error",
          message: "Sorry - we had a problem. Please try again or contact support.",
        });
        return { error: "Could not create rep." };
      }
      trackEvent(
        "Sales Reps Added",
        new SalesRepsEventEntity({
          service: "Retailer",
          component: "Distributor Settings New Sales Rep",
          id: res.id,
        }),
      );

      // Needs to wrap response into a object with data attribute because
      // there are components using this callback waiting for the old/$http
      // response object.
      return { data: res };
    } catch (error) {
      errorCallback(error);
    }
  };

  const updateMobileMode = (name: string) => {
    mobileMode = name == "mobile";
  };

  const openConnectDistributorModal = (): void => {
    if (restrictRepAccess()) return;

    showConnectDistributorModal = true;
  };

  const addSalesRepErrorCallback = function (res) {
    const defaultError = {
      messages: ["Sorry - we had a problem. Please try again or contact support."],
    };
    const data = res || {};
    formErrors = data.errors || defaultError;
  };

  const filterProductsByVintage = (allProducts: Product[], vintageSelection: string | number) => {
    return vintageSelection === "Current"
      ? allProducts.filter(product => !product.vintage_year)
      : allProducts.filter(product => String(product.vintage_year) === String(vintageSelection));
  };

  const prepareProductData = function (
    allProducts: Product[],
    vintageSelection: string | number,
    discounts: DiscountResult[],
  ) {
    if (!(distributor || {}).id) {
      return;
    }

    let displayProducts = allProducts;

    const hasAnyVintages = allProducts.some(product => Boolean(product.vintage_year));
    if (hasAnyVintages && vintageSelection) {
      displayProducts = filterProductsByVintage(allProducts, vintageSelection);
    } else if (hasAnyVintages) {
      displayProducts = [];
    }

    const data = ProductHelpers.prepareContainers(
      allProducts,
      displayProducts,
      discounts,
      usesFuturePricingWithMinBottle,
      preSelectedContainerType,
    );

    if (!data) return null;

    return {
      ...data,
      selectedVintageProduct: vintageSelection ? displayProducts[0] : null,
    };
  };

  callbacks.selectVintage = (vintage: string) => {
    selectedVintage = String(vintage);
    const { productId, ...restParams } = $params;
    $redirect(`/products/${productId}`, {
      ...restParams,
      vintage: vintage,
    });
  };

  callbacks.selectContainerType = (containerType: string) => {
    selectedContainerType = containerType;
  };

  if (preSelectedVintage) {
    callbacks.selectVintage(preSelectedVintage);
  }

  const pageName: string = "product_detail";
  const pageValue: string = productLine.id.toString();
  const options = {
    method: "GET",
    url: "/api/retailer/discovery_panel/discovery_config",
    data: {
      page_name: pageName,
      page_value: pageValue,
      without_matched_distributor: showDualProductExperience || !productLine.distributor?.id,
    },
  };

  let discoveryPanelConfig: any[] = [];
  HttpPromise(options).then(
    function success(resp) {
      discoveryPanelConfig = resp;
    },
    function error() {
      discoveryPanelConfig = [];
    },
  );

  const decideForceDistributorSelection = () => {
    const distributorNotInChoices = !distributorChoices.some(d => d.id === distributor?.id);
    const alwaysChoose = distributor?.always_choose_distributor || distributor?.state === "GA";
    if (distributor?.id && distributorNotInChoices && alwaysChoose) {
      alwaysChooseForceDistributorSelection = true;
    }
  };
  $: {
    if (
      distributorChoices.length > 0 &&
      (!showDualProductExperience || (showDualProductExperience && distributor?.id))
    ) {
      decideForceDistributorSelection();
    }
  }

  $: {
    const data = prepareProductData(products, selectedVintage, $DiscountsLookupStore);
    if (data) {
      ({
        productsByContainer,
        selectedContainerType,
        showDeals,
        showRecentlyOrdered,
        hasDiscount,
        containerTypes,
        selectedVintageProduct,
      } = data);
    }
  }

  let authService = getControlStateAuthenticationService();

  let isSpiritProductLine: boolean = productLine?.product_type_name == "Spirits";

  let hideAllDistributorInfo: boolean = false;
  let retailerUserAuthStatus: string = "";
  $: {
    hideAllDistributorInfo =
      isSpiritProductLine &&
      authService?.shouldHideAllDistributorInfo?.($RetailerUserAuthenticationsStore, [
        distributor?.id,
      ]);
    retailerUserAuthStatus = authService?.getRetailerUserAuthenticationStatus?.(
      $RetailerUserAuthenticationsStore,
      [distributor?.id],
    );
  }
</script>

<PageTitle title={`${productLine.name} | Provi`} />
<div class="fizz-styles background-white" style="padding-bottom: 1rem;">
  <FormErrors errors={formErrors} />

  {#if hideAllDistributorInfo && mobileMode && authService?.ctaComponent}
    <div class="margin-container">
      {#if retailerUserAuthStatus == "pending"}
        <div style="padding-top: 24px;">
          <div
            class="fizz-radius-large fizz-inset-square-32"
            style="background-color: rgb(238, 248, 246); "
          >
            <svelte:component
              this={authService?.ctaComponent}
              authStatus={retailerUserAuthStatus}
              {authService}
            />
          </div>
        </div>
      {:else if retailerUserAuthStatus == "invalid"}
        <svelte:component
          this={authService?.ctaComponent}
          authStatus={retailerUserAuthStatus}
          {authService}
        />
      {/if}
    </div>
  {/if}

  <div class="fizz-inset-vertical-24 margin-container">
    <BreadcrumbNavigation subcategoryIdProp={productLine.subcategory_id} />
  </div>

  <section class="product-detail margin-container" use:OnLayoutChange={updateMobileMode}>
    <div
      class="margin-bottom--small"
      class:fizz-flex-vertical={mobileMode}
      class:fizz-flex-row={!mobileMode}
      style="margin-inline: auto;"
    >
      <LeftPane
        {accountDataLoaded}
        bind:hideAllDistributorInfo
        {mobileMode}
        {productLine}
        {hasDiscount}
        {distributor}
        {selectedVintageProduct}
        {openConnectDistributorModal}
        {retailerDistributor}
        {salesReps}
        {discoveryPanelConfig}
        {pageName}
        {pageValue}
        distributors={distributorChoices}
        {showDualProductExperience}
        {sevenFiftyProduct}
        {vendorProduct}
      />
      <RightPane
        bind:hideAllDistributorInfo
        bind:retailerUserAuthStatus
        {pricingDataReady}
        {productLine}
        {products}
        {callbacks}
        {distributor}
        {selectedVintage}
        {selectedContainerType}
        {productsByContainer}
        {showDeals}
        {showRecentlyOrdered}
        {containerTypes}
        {Cart}
        {alwaysChooseForceDistributorSelection}
        {showDualProductExperience}
        distributors={distributorChoices}
        {selectDistributor}
      />

      {#if mobileMode}
        <DiscoveryPanel
          carouselCardsShown={4}
          configuration={discoveryPanelConfig}
          {pageName}
          {pageValue}
          {mobileMode}
        />
      {/if}
    </div>
  </section>

  {#key distributor?.id}
    <ConnectDistributorModal
      submit={callbacks.distributorSubmit}
      addNewSalesRep={callbacks.addNewSalesRep}
      providedDistributor={true}
      bind:showConnectDistributorModal
      {addSalesRepErrorCallback}
      submitErrorCallback={addSalesRepErrorCallback}
      selectedDistributor={distributor}
    />
  {/key}
</div>
